import { useCallback, useRef } from 'react';
import { dispatcher } from 'dispatcher';
import { useDispatch } from 'react-redux';
import { getMessageFromActions } from '@core/js/hooks/server';
import { storageService } from '@core/js/services/storage.service';
import { showTermsConditionsConfirmationPopupAction } from '@core/js/components/popups/TermsConditionsConfirmationPopup';
import * as pas from '@core/js/hooks/auth/pas';
import { useLogout } from '@core/js/hooks/auth/hooks/useLogout';
import { useContinueLoginWithSessionToken } from '@core/js/hooks/auth/hooks/useContinueLoginWithSessionToken';

export const useValidateSessionForFido = () => {
    const logout = useLogout();
    const currentValidations = useRef(new Set());
    const dispatch = useDispatch();
    const continueLoginWithSessionToken = useContinueLoginWithSessionToken();

    const validationStart = useCallback(type => currentValidations.current.add(type), [currentValidations]);

    const validationDone = useCallback(type => currentValidations.current.delete(type), [currentValidations]);

    const continueLogin = useCallback(async ({ credentials, afterRegisterLogin, isAfterLoginWithCredentials }) => {
        if (currentValidations.current.size > 0) {
            return;
        }

        const { data: { sessionToken: { sessionToken } } } = await pas.getTempToken();

        await continueLoginWithSessionToken({ ...credentials, sessionToken, afterRegisterLogin, isAfterLoginWithCredentials });
    }, [currentValidations, continueLoginWithSessionToken]);

    const reLogin = useCallback(async ({ credentials, afterRegisterLogin, isAfterLoginWithCredentials }) => {
        const pasLoginResponse = await pas.login(credentials);
        const {
            sessionValidationData,
            sessionToken: { sessionToken = '' } = {}
        } = pasLoginResponse?.data || {};

        if (sessionValidationData?.SessionValidationBy2FAData) {
            return;
        } else if (sessionToken) {
            await continueLoginWithSessionToken({
                ...credentials, sessionToken, afterRegisterLogin, isAfterLoginWithCredentials
            });

            return;
        }

        return Promise.reject(pasLoginResponse);
    }, [continueLoginWithSessionToken]);

    const acceptTC = useCallback(({ termVersionReference, credentials, afterRegisterLogin, isAfterLoginWithCredentials }) => {
        (async () => {
            try {
                await pas.setTerms({
                    accept: true,
                    termVersionReference
                });
                validationDone('acceptTerms');
                await continueLogin({ credentials, afterRegisterLogin, isAfterLoginWithCredentials });
            } catch {
                logout(true);
            }
        })();
    }, [continueLogin, validationDone, logout]);

    const sendFitToPay = useCallback(({ fitToPlay, credentials, afterRegisterLogin, isAfterLoginWithCredentials }) => {
        (async () => {
            try {
                await pas.validateFitToPlayToken(fitToPlay);
                validationDone('fitToPlay');
                await continueLogin({ credentials, afterRegisterLogin, isAfterLoginWithCredentials });
            } catch {
                logout(true);
            }
        })();
    }, [continueLogin, validationDone, logout]);

    return useCallback((pasLoginResponse, credentials, options) => {
        const { afterRegisterLogin, isAfterLoginWithCredentials } = options || {};
        const { sessionValidationData } = pasLoginResponse?.data || {};

        if (!sessionValidationData) {
            return true;
        }

        if (sessionValidationData.SessionValidationByTCVersionData) {
            let ensureLogout = true;
            const { tcVersionUrl, termVersionReference } = sessionValidationData.SessionValidationByTCVersionData?.[0] || {};
            const tcVersionUrlObject = new URL(tcVersionUrl);
            const tcVersion = tcVersionUrlObject.searchParams.get('version') || '';
            validationStart('acceptTerms');

            dispatch(showTermsConditionsConfirmationPopupAction({
                    tcVersion,
                    onAccept: async () => {
                        await acceptTC({
                            termVersionReference,
                            credentials,
                            afterRegisterLogin,
                            isAfterLoginWithCredentials
                        });
                        ensureLogout = false;
                    },
                    onDecline: () => {
                        if (ensureLogout) {
                            return logout(true);
                        }
                        return Promise.resolve();
                    }
            }));
        }

        if (sessionValidationData.SessionValidationByPasswordChangeData) {
            let ensureLogout = true;
            const { requiredFields = 'newPassword' } = sessionValidationData.SessionValidationByPasswordChangeData[0] || {};
            const showOldPassword = requiredFields
                .split(',')
                .map((field: string) => field.trim())
                .includes('oldPassword');

            validationStart('changePassword');

            dispatcher.dispatch('app:message', {
                ID: 'changePassword',
                data: {
                    showOldPassword,
                    onSuccess: async (newPassword: string) => {
                        validationDone('changePassword');

                        credentials.password = newPassword;

                        await continueLogin({ credentials, afterRegisterLogin, isAfterLoginWithCredentials });
                        ensureLogout = false;
                    },
                    onClose: () => {
                        if (ensureLogout) {
                            logout(true);
                        }
                    }
                }
            });
        }

        if (sessionValidationData.SessionValidationBy2FAData) {
            let ensureLogout = true;
            validationStart('twoFactorAuthentication');

            dispatcher.dispatch('app:message', {
                ID: 'twoFactorAuthentication',
                data: {
                    onSuccess: async () => {
                        validationDone('twoFactorAuthentication');
                        await continueLogin({ credentials, afterRegisterLogin, isAfterLoginWithCredentials });
                        ensureLogout = false;
                    },
                    onClose: () => {
                        if (ensureLogout) {
                            logout(true);
                        }
                    },
                    resendHandler: () => reLogin({ credentials, afterRegisterLogin, isAfterLoginWithCredentials }),
                    title: getMessageFromActions(pasLoginResponse)
                }
            });
        }

        if (sessionValidationData.SessionValidationByFitToPlayData) {
            validationStart('fitToPlay');

            if (storageService.session.hasItem('fitToPlay')) {
                const fitToPlay = storageService.session.getItem('fitToPlay');

                storageService.session.removeItem('fitToPlay');

                sendFitToPay({ fitToPlay, credentials, afterRegisterLogin, isAfterLoginWithCredentials });
            } else {
                dispatcher.dispatch('app:message', {
                    ID: 'fitToPlay',
                    data: {
                        onAccept: () => sendFitToPay({
                            fitToPlay: true,
                            credentials,
                            afterRegisterLogin,
                            isAfterLoginWithCredentials
                        }),
                        onDecline: () => sendFitToPay({
                            fitToPlay: false,
                            credentials,
                            afterRegisterLogin,
                            isAfterLoginWithCredentials
                        })
                    }
                });
            }
        }

        return !currentValidations.current.size;
    }, [
        continueLogin, reLogin, acceptTC, sendFitToPay,
        validationStart, validationDone, dispatch, logout
    ]);
};
